import { useEffect, useState, type ReactNode } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import { renderChevronAnimation } from "@shared/selects/utils";

import { sidebarItemClasses } from "./nav-link";
import { sidebarIconClasses } from "./sidebar-item";

interface Props {
  readonly isHidden?: boolean;
  readonly icon: ReactNode;
  readonly expanded: boolean;
  readonly useDevelopmentAnalyticsData: boolean;
  readonly children: ReactNode;
  readonly title: string;
  readonly isNew?: boolean;
}

export const SidebarItemWithAccordion = ({
  isHidden,
  icon,
  expanded,
  children,
  isNew,
  title,
  useDevelopmentAnalyticsData,
}: Props) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  useEffect(() => {
    if (!expanded) {
      setIsAccordionOpen(false);
    }
  }, [expanded]);
  if (isHidden) {
    return null;
  }

  const toggleAccordion = () => setIsAccordionOpen(!isAccordionOpen);

  return (
    <div>
      <div className={clsx(sidebarItemClasses)} onClick={toggleAccordion}>
        <div className="flex items-center justify-between">
          <div className="relative">
            <span className={sidebarIconClasses(useDevelopmentAnalyticsData)}>
              {icon}
              {isNew && (
                <div
                  className={
                    "absolute -bottom-2  left-1/2  transform -translate-x-1/2 " +
                    "text-xxs border-interstellar border-2 bg-pluto text-interstellar rounded-lg px-1 "
                  }
                >
                  NEW
                </div>
              )}
            </span>
          </div>
          <motion.span
            className={clsx("ml-2", {
              "pointer-events-none hidden": !expanded,
            })}
            animate={{ opacity: expanded ? 1 : 0 }}
            transition={{ duration: 0.1 }}
          >
            {title}
          </motion.span>
          {expanded &&
            renderChevronAnimation(
              isAccordionOpen,
              false,
              null,
              "ml-16 text-xs",
            )}
        </div>
      </div>
      <AnimatePresence initial={false}>
        {isAccordionOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ ease: "easeOut", duration: 0.2 }}
            className="overflow-hidden ml-10 space-y-2"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
